//custom-preset.ts

import { $dt, definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { AuraBaseDesignTokens } from '@primeng/themes/aura/base';
import { Preset } from '@primeng/themes/types';

const elisoturquoise = {
  50: 'rgb(243, 253, 252)',
  100: 'rgb(197, 246, 239)',
  200: 'rgb(151, 240, 226)',
  300: 'rgb(105, 233, 213)',
  400: 'rgb(59, 226, 201)',
  500: '#0ddbbc',
  600: 'rgb(11, 186, 160)',
  700: 'rgb(9, 153, 132)',
  800: 'rgb(7, 120, 103)',
  900: 'rgb(5, 88, 75)',
  950: 'rgb(3, 55, 47)',
};
const elisogreen = {
  50: '#f4f6f6',
  100: '#cbd5d1',
  200: '#a3b4ad',
  300: '#7a9389',
  400: '#517165',
  500: '#285041',
  600: '#224437',
  700: '#1c382e',
  800: '#162c24',
  900: '#14201a',
  950: '#0d1410',
};

const preset: Preset<AuraBaseDesignTokens> = {
  extend: {
    primitive: {
      elisoturquoise,
      elisogreen,
    },
    semantic: {
      secondary: {
        50: '{elisoturquoise.50}',
        100: '{elisoturquoise.100}',
        200: '{elisoturquoise.200}',
        300: '{elisoturquoise.300}',
        400: '{elisoturquoise.400}',
        500: '{elisoturquoise.500}',
        600: '{elisoturquoise.600}',
        700: '{elisoturquoise.700}',
        800: '{elisoturquoise.800}',
        900: '{elisoturquoise.900}',
        950: '{elisoturquoise.950}',
      },
    },
  },
  primitive: {
    borderRadius: {
      xl: '14px',
    },
  },
  semantic: {
    primary: {
      50: '{elisogreen.50}',
      100: '{elisogreen.100}',
      200: '{elisogreen.200}',
      300: '{elisogreen.300}',
      400: '{elisogreen.400}',
      500: '{elisogreen.500}',
      600: '{elisogreen.600}',
      700: '{elisogreen.700}',
      800: '{elisogreen.800}',
      900: '{elisogreen.900}',
      950: '{elisogreen.950}',
    },
    colorScheme: {
      light: {
        text: { color: '{primary.500}' },
        surface: {
          50: '{neutral.50}',
          100: '{neutral.100}',
          200: '{neutral.200}',
          300: '{neutral.300}',
          400: '{neutral.400}',
          500: '{neutral.500}',
          600: '{neutral.600}',
          700: '{neutral.700}',
          800: '{neutral.800}',
          900: '{neutral.900}',
          950: '{neutral.950}',
        },
      },
    },
    formField: {
      borderRadius: '{border.radius.xl}',
      paddingY: '0.4rem',
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: '{secondary.500}',
              hoverBackground: '{secondary.600}',
              activeBackground: '{secondary.700}',
            },
          },
          text: {
            contrast: {
              color: '{secondary.500}',
              hoverBackground: '#b1ff002a', //secondary transparent
              activeBackground: '#b1ff002a', //secondary transparent
            },
          },
        },
      },
    },
    datatable: {
      headerCell: {
        padding: '0.3rem 1rem',
      },
      bodyCell: {
        padding: '0.3rem 1rem',
      },
      columnTitle: {
        fontWeight: '300',
      },
      row: {
        stripedBackground: '{surface.100}',
        selectedBackground: '{primary.100}',
      },
    },
    menubar: {
      root: {
        borderRadius: '0',
      },
      colorScheme: {
        light: {
          root: {
            background: '#fff',
            borderColor: 'transparent',
          },
          item: {
            color: '#fff',
            focusBackground: 'transparent',
          },
        },
      },
      css: ({ dt }: { dt: typeof $dt }) => `
        .layout-topbar {
            box-shadow: 0px 2px 15px -3px rgba(0, 0, 0, 0.1);
        }
        `,
    },
    panelmenu: {
      root: { gap: '0' },
      panel: {
        first: {
          borderWidth: '0',
        },
        last: {
          borderWidth: '0',
        },
        borderWidth: '0',
        padding: '0 0.25rem',
      },
      item: {
        padding: '0.25rem 0.5rem',
      },
      colorScheme: {
        light: {
          root: {},
        },
      },
      css: ({ dt }: { dt: typeof $dt }) => `
        .layout-sidebar {
            box-shadow: 0px 2px 15px -3px rgba(0, 0, 0, 0.1);
        }
        `,
    },
    card: {
      title: {
        fontWeight: '700',
      },
      colorScheme: {
        light: {
          root: {
            shadow: '0px 2px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    chip: {
      root: {
        paddingY: '0.3rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '#f5f5f5',
          },
        },
      },
    },
    autocomplete: {
      chip: { borderRadius: '16px' },
    },
    breadcrumb: {
      root: {
        padding: '1rem 0',
        background: 'transparent',
      },
    },
  },
};

export const colors = {
  palette: [
    '#285041',
    '#0ddbbc',
    '#1d816c',
    '#5f8d82',
    '#bbd9d3',
    '#4C7D77',
    '#24353D',
    '#DDFF91',
    '#0E1518',
    '#7CB300',
    '#476600',
    '#166DFF',
    '#3A03B0',
    '#C4256A',
    '#D12C34',
    '#EA7D1F',
    '#F1AC27',
  ],
  map: {
    location: elisoturquoise[500],
  },
};

export const links = {
  logoSrc: 'assets/images/eliso-logo.png',
  logoAlt: 'Eliso Logo',
  impressum: 'https://eliso.io/impressum/',
};

export const CustomPreset = definePreset(Aura, preset);
